import {
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  rem,
  Select,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import {
  AVAILABILITY_SLOTS,
  mailboxSiblings,
  tidyBlankLines,
  type VolunteerImportPlan,
  type VolunteerImportRow,
} from '@reditelji/shared';
import { useEffect, useRef, useState } from 'react';
import {
  useTriage,
  useVolunteerMutations,
  type MessageRow,
  type VolunteerRow,
} from '../api/volunteers';

/**
 * CSV import — the signup Google Sheet, in two steps.
 *
 * The roster is a live list and coordinators edit the same people in the app,
 * so nothing is written until the diff has been read: the preview shows what
 * each row would change, and any row can be excluded before confirming.
 */
export function ImportModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const mut = useVolunteerMutations();
  const [csv, setCsv] = useState('');
  const [plan, setPlan] = useState<VolunteerImportPlan | null>(null);
  const [skip, setSkip] = useState<Set<number>>(new Set());
  const [done, setDone] = useState<{ created: number; updated: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function reset() {
    setCsv('');
    setPlan(null);
    setSkip(new Set());
    setDone(null);
  }

  useEffect(() => {
    if (!opened) reset();
  }, [opened]);

  const willWrite = plan
    ? plan.rows.filter((r) => ACTIONABLE.has(r.action) && !skip.has(r.rowNum)).length
    : 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Uvoz prostovoljcev (CSV)"
      size="xl"
      styles={{ body: { paddingTop: 4 } }}
    >
      {done ? (
        <Stack gap={10}>
          <Text size="sm">
            Uvoženo: <b>{done.created}</b> novih, <b>{done.updated}</b> posodobljenih.
          </Text>
          <Group justify="flex-end" gap={8}>
            <Button size="compact-sm" variant="default" onClick={reset}>
              Uvozi še eno datoteko
            </Button>
            <Button size="compact-sm" onClick={onClose}>
              Zapri
            </Button>
          </Group>
        </Stack>
      ) : plan ? (
        <Stack gap={10}>
          <PlanSummary plan={plan} />
          <div style={{ maxHeight: '52vh', overflowY: 'auto', paddingRight: 4 }}>
            <Stack gap={12}>
              {GROUPS.map((group) => {
                const rows = plan.rows.filter((r) => r.action === group.action);
                if (!rows.length) return null;
                return (
                  <div key={group.action}>
                    <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
                      {group.title} ({rows.length})
                    </Text>
                    <Stack gap={4}>
                      {rows.map((row) => (
                        <PlanRowCard
                          key={`${row.action}-${row.rowNum}-${row.name}`}
                          row={row}
                          excluded={skip.has(row.rowNum)}
                          onToggle={() =>
                            setSkip((prev) => {
                              const next = new Set(prev);
                              if (next.has(row.rowNum)) next.delete(row.rowNum);
                              else next.add(row.rowNum);
                              return next;
                            })
                          }
                        />
                      ))}
                    </Stack>
                  </div>
                );
              })}
            </Stack>
          </div>
          <Group justify="space-between" gap={8}>
            <Text size="xs" c="dimmed">
              Nič se ne zapiše, dokler ne potrdiš.
            </Text>
            <Group gap={8}>
              <Button size="compact-sm" variant="default" onClick={() => setPlan(null)}>
                Nazaj
              </Button>
              <Button
                size="compact-sm"
                loading={mut.applyCsv.isPending}
                disabled={willWrite === 0}
                onClick={() => {
                  mut.applyCsv.mutate(
                    { csv, skipRows: [...skip] },
                    {
                      onSuccess: (res) => {
                        setDone({ created: res.created, updated: res.updated });
                      },
                    },
                  );
                }}
              >
                Uvozi ({willWrite})
              </Button>
            </Group>
          </Group>
        </Stack>
      ) : (
        <Stack gap={10}>
          <Text size="xs" c="dimmed">
            V Google preglednici s prijavami izberi <b>File → Download → CSV</b> (prvi zavihek —
            drugi je legenda terminov) in datoteko spusti sem ali njeno vsebino prilepi v polje.
            Stolpci se ujamejo po imenih iz preglednice. Prostovoljce ujamemo po imenu in priimku,
            e-naslov je razsodnik, kadar sta dva enaka. Datoteka se najprej razbere za predogled —
            v bazo se zapiše šele ob potrditvi.
          </Text>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const file = e.dataTransfer.files[0];
              if (file) void file.text().then(setCsv);
            }}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `1.5px dashed ${dragging ? 'oklch(0.62 0.09 245)' : 'oklch(0.88 0.006 95)'}`,
              background: dragging ? 'oklch(0.97 0.02 245)' : 'oklch(0.985 0.004 95)',
              borderRadius: 10,
              padding: '18px 14px',
              textAlign: 'center',
              cursor: 'pointer',
            }}
          >
            <Text size="sm" fw={600}>
              Povleci datoteko .csv sem
            </Text>
            <Text size="xs" c="dimmed">
              ali klikni za izbiro
            </Text>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => {
                const file = e.currentTarget.files?.[0];
                if (file) void file.text().then(setCsv);
                e.currentTarget.value = '';
              }}
            />
          </div>

          <Textarea
            value={csv}
            onChange={(e) => setCsv(e.currentTarget.value)}
            autosize
            minRows={6}
            maxRows={14}
            styles={{ input: { fontFamily: 'var(--rr-font-mono)', fontSize: rem(12.5) } }}
            placeholder={'ime,priimek,telefon,e-poštni naslov,spol,kraj bivanja,…\nJanez,Novak,041 123 123,janez.novak@primer.si,moški,Ljubljana,…'}
          />
          <Group justify="flex-end">
            <Button
              size="compact-sm"
              disabled={!csv.trim()}
              loading={mut.previewCsv.isPending}
              onClick={() => {
                mut.previewCsv.mutate(csv, {
                  onSuccess: (next) => {
                    setPlan(next);
                    setSkip(new Set());
                  },
                });
              }}
            >
              Predogled
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

/** Actions that write something; the rest of the preview is there to be read. */
const ACTIONABLE = new Set<VolunteerImportRow['action']>(['create', 'update']);

const GROUPS: { action: VolunteerImportRow['action']; title: string }[] = [
  { action: 'error', title: 'Napake' },
  { action: 'create', title: 'Novi' },
  { action: 'update', title: 'Spremenjeni' },
  { action: 'only-in-app', title: 'Samo v aplikaciji' },
  { action: 'unchanged', title: 'Nespremenjeni' },
];

function PlanSummary({ plan }: { plan: VolunteerImportPlan }) {
  const chips: { label: string; value: number; tone?: string }[] = [
    { label: 'novih', value: plan.summary.create },
    { label: 'spremenjenih', value: plan.summary.update },
    { label: 'nespremenjenih', value: plan.summary.unchanged },
    { label: 'napak', value: plan.summary.error, tone: 'red.7' },
    { label: 'samo v aplikaciji', value: plan.summary.onlyInApp },
  ];
  return (
    <Group gap={6}>
      {chips.map((c) => (
        <Text key={c.label} size="xs" c={c.value > 0 ? c.tone : 'dimmed'}>
          <b>{c.value}</b> {c.label}
        </Text>
      ))}
    </Group>
  );
}

/** Slot ids read as "SOB dopoldne", not as `d1-08-12`. */
function fieldLabel(field: string): string {
  if (!field.startsWith('availability.')) return FIELD_LABELS[field] ?? field;
  const slot = AVAILABILITY_SLOTS.find((s) => s.id === field.slice('availability.'.length));
  return slot ? `${slot.sheetDay} ${slot.name} (${slot.hours})` : field;
}

const FIELD_LABELS: Record<string, string> = {
  phone: 'telefon',
  email: 'e-naslov',
  lang: 'jezik',
  gender: 'spol',
  residence: 'kraj bivanja',
  age: 'starost',
  experience: 'izkušenost',
  canMultiDay: 'lahko večkrat',
  otherTasks: 'druge naloge',
  wishes: 'želje',
};

/** A changed field's value as one short line. Every compared field is a scalar. */
function cell(value: unknown): string {
  if (value === null || value === '' || value === undefined) return '—';
  if (value === true) return 'da';
  if (value === false) return 'ne';
  if (typeof value === 'number') return String(value);
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function PlanRowCard({
  row,
  excluded,
  onToggle,
}: {
  row: VolunteerImportRow;
  excluded: boolean;
  onToggle: () => void;
}) {
  const canExclude = ACTIONABLE.has(row.action);
  return (
    <div
      style={{
        border: '1px solid oklch(0.92 0.005 95)',
        borderRadius: 8,
        padding: '6px 9px',
        opacity: excluded ? 0.5 : 1,
      }}
    >
      <Group gap={8} wrap="nowrap" align="flex-start">
        {canExclude ? (
          <Checkbox
            size="xs"
            mt={2}
            checked={!excluded}
            onChange={onToggle}
            aria-label={`Vključi ${row.name}`}
          />
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="sm" fw={600}>
              {row.name || '(brez imena)'}
            </Text>
            {row.rowNum > 0 ? (
              <Text size="xs" c="dimmed">
                vrstica {row.rowNum}
              </Text>
            ) : null}
            {row.reactivates ? (
              <Text size="xs" c="orange.7" fw={600}>
                ponovna aktivacija
              </Text>
            ) : null}
          </Group>

          {row.error ? (
            <Text size="xs" c="red.7">
              {row.error}
            </Text>
          ) : null}

          {row.changes.length ? (
            <Stack gap={1} mt={3}>
              {row.changes.map((c) => (
                <Text key={c.field} size="xs" c="dimmed">
                  {fieldLabel(c.field)}: <s>{cell(c.before)}</s> → <b>{cell(c.after)}</b>
                </Text>
              ))}
            </Stack>
          ) : null}

          {row.tags.length ? (
            <Text size="xs" c="dimmed" mt={3}>
              oznake: {row.tags.join(', ')}
            </Text>
          ) : null}

          {row.notices.map((n) => (
            <ImportNote key={n}>{n}</ImportNote>
          ))}
        </div>
      </Group>
    </div>
  );
}

function ImportNote({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 5,
        padding: '5px 8px',
        borderRadius: 7,
        background: 'oklch(0.99 0.012 75)',
        border: '1px solid oklch(0.93 0.03 75)',
        color: 'oklch(0.45 0.09 75)',
        fontSize: rem(11.5),
        lineHeight: 1.45,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Triage — inbound mail whose sender matched nobody (SPEC §2 IMAP ingest).
 *
 * Attaching it to a person who shares their inbox offers the rest of the
 * household, ticked: a mail in a shared mailbox is in every member's thread,
 * and a stray one attached by hand should end up the same way.
 */
export function TriageModal({
  opened,
  volunteers,
  onClose,
}: {
  opened: boolean;
  volunteers: VolunteerRow[];
  onClose: () => void;
}) {
  const triage = useTriage();
  return (
    <Modal opened={opened} onClose={onClose} title="Nabiralnik — nerazvrščena pošta" size="lg">
      {triage.isPending ? (
        <Loader size="xs" mx="auto" display="block" />
      ) : triage.data!.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          Ni nerazvrščene pošte. Dohodna e-pošta z znanih naslovov se samodejno pripne
          prostovoljcu — in vsem, ki ta naslov delijo.
        </Text>
      ) : (
        <Stack gap={8}>
          {triage.data!.map((m) => (
            <TriageRow key={m.id} message={m} volunteers={volunteers} />
          ))}
        </Stack>
      )}
    </Modal>
  );
}

function TriageRow({ message: m, volunteers }: { message: MessageRow; volunteers: VolunteerRow[] }) {
  const mut = useVolunteerMutations();
  // The person picked, with the household members to file the mail with —
  // all of them from the start, as ingest would have done.
  const [pick, setPick] = useState<{ id: string; alsoTo: string[] } | null>(null);
  const siblingsOf = (id: string) => {
    const v = volunteers.find((x) => x.id === id);
    return v ? mailboxSiblings(v, volunteers) : [];
  };
  const siblings = pick ? siblingsOf(pick.id) : [];
  const assign = (id: string, alsoTo: string[]) =>
    mut.assignTriage.mutate({ messageId: m.id, volunteerId: id, alsoTo });

  return (
    <div style={{ border: '1px solid var(--rr-border)', borderRadius: 8, padding: '8px 10px' }}>
      <Group gap={6}>
        <Text size="xs" fw={600} style={{ fontFamily: 'var(--rr-font-mono)' }}>
          {m.fromAddress || '(neznan pošiljatelj)'}
        </Text>
        <Text size="xs" c="dimmed">
          {new Date(m.at).toLocaleString('sl-SI', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })}
        </Text>
      </Group>
      {m.subject ? (
        <Text size="xs" fw={600}>
          {m.subject}
        </Text>
      ) : null}
      <Text size="xs" lineClamp={3} style={{ whiteSpace: 'pre-wrap' }}>
        {tidyBlankLines(m.body)}
      </Text>
      <Select
        size="xs"
        mt={6}
        placeholder="Pripni prostovoljcu…"
        searchable
        value={pick?.id ?? null}
        data={volunteers.map((v) => ({ value: v.id, label: v.name }))}
        onChange={(id) => {
          if (!id) return setPick(null);
          const others = siblingsOf(id);
          // Nobody else on the address: attach at once, as before. A household
          // waits for the tick list below.
          if (others.length === 0) return assign(id, []);
          setPick({ id, alsoTo: others.map((s) => s.id) });
        }}
      />
      {pick && siblings.length > 0 ? (
        <Stack gap={4} mt={6}>
          <Text size="xs" c="dimmed">
            Isti predal — pripni tudi:
          </Text>
          {siblings.map((s) => (
            <Checkbox
              key={s.id}
              size="xs"
              label={s.name}
              checked={pick.alsoTo.includes(s.id)}
              onChange={(e) =>
                setPick({
                  id: pick.id,
                  alsoTo: e.currentTarget.checked
                    ? [...pick.alsoTo, s.id]
                    : pick.alsoTo.filter((x) => x !== s.id),
                })
              }
            />
          ))}
          <Button
            size="compact-xs"
            style={{ alignSelf: 'flex-start' }}
            loading={mut.assignTriage.isPending}
            onClick={() => assign(pick.id, pick.alsoTo)}
          >
            Pripni
          </Button>
        </Stack>
      ) : null}
    </div>
  );
}
